import { Alert } from '@snitch/ui/alert'
import { GoogleIcon } from '@snitch/ui/brand-icons'
import { Button } from '@snitch/ui/button'
import { Divider } from '@snitch/ui/divider'
import { Field } from '@snitch/ui/field'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Mail, Send, TicketPercent } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AuthShell } from '../components/auth-shell.tsx'
import { api } from '../lib/api.ts'
import { sendEmailCode, startOAuth } from '../lib/auth.ts'
import {
  forgetInvitation,
  invitationFromTarget,
  invitationTarget,
  normalizeInviteCode,
  pendingInvitation,
  rememberInvitation,
} from '../lib/invitations.ts'
import { currentUser, safeNext } from '../lib/session.ts'

interface LoginSearch {
  next?: string
  error?: string
  provider?: string
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    next: typeof search.next === 'string' ? search.next : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
    provider: typeof search.provider === 'string' ? search.provider : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const pending = pendingInvitation()
    if (await currentUser(context.queryClient))
      throw redirect({
        href: safeNext(
          search.next ?? (pending ? invitationTarget(pending.code, pending.via) : undefined),
        ),
      })
  },
  component: Login,
})

const PROVIDER_NAME: Record<string, string> = { telegram: 'Telegram', google: 'Google' }

/** Messages for ?error= after a failed redirect sign-in. */
function notice(error: string | undefined, provider: string | undefined) {
  const name = PROVIDER_NAME[provider ?? ''] ?? 'этот сервис'
  switch (error) {
    case undefined:
      return null
    case 'session_expired':
      return {
        tone: 'info' as const,
        text: 'Сессия истекла — войдите снова. Тариф и устройства на месте.',
      }
    case 'cancelled':
      return { tone: 'info' as const, text: `Вход через ${name} отменён. Выберите способ ещё раз.` }
    case 'unavailable':
      return {
        tone: 'error' as const,
        text: `Вход через ${name} пока недоступен. Выберите другой способ.`,
      }
    case 'suspended':
      return {
        tone: 'error' as const,
        text: 'Аккаунт заблокирован. Напишите в поддержку — разберёмся.',
      }
    default:
      return {
        tone: 'error' as const,
        text: `Не получилось войти через ${name}. Попробуйте ещё раз или выберите другой способ.`,
      }
  }
}

/** "Вход · …" — Telegram, Google or an email code; the account is created on first sign-in. */
function Login() {
  const { next, error, provider } = Route.useSearch()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string>()
  const [storedInvitation] = useState(pendingInvitation)
  const fromLink =
    invitationFromTarget(next) ??
    (error && storedInvitation?.via !== 'code' ? storedInvitation : null)
  const [friendCode, setFriendCode] = useState(() => storedInvitation?.code ?? '')
  const [friendError, setFriendError] = useState<string>()
  const preview = useQuery({
    queryKey: ['referrals', 'preview', fromLink?.code, 'guest'],
    queryFn: () => api.referrals.preview({ code: fromLink?.code ?? '' }),
    enabled: Boolean(fromLink),
  })
  useEffect(() => {
    const invite = invitationFromTarget(next)
    if (invite) rememberInvitation(invite)
  }, [next])
  const alert = notice(error, provider)
  function target() {
    if (fromLink) {
      rememberInvitation(fromLink)
      return invitationTarget(fromLink.code, fromLink.via)
    }
    if (!friendCode.trim()) {
      forgetInvitation()
      return safeNext(next)
    }
    const code = normalizeInviteCode(friendCode)
    if (!code) {
      setFriendError('От 3 до 32 латинских букв, цифр или дефисов.')
      return null
    }
    const via = storedInvitation?.code === code ? storedInvitation.via : 'code'
    rememberInvitation({ code, via })
    return invitationTarget(code, via)
  }
  function oauth(method: 'telegram' | 'google') {
    const next = target()
    if (next) startOAuth(method, { next })
  }

  const send = useMutation({
    mutationFn: (input: { email: string; next: string }) => sendEmailCode(input.email),
    onSuccess: (result, input) => {
      if (result.ok) {
        void navigate({
          to: '/login/code',
          search: { email: input.email.trim(), next: input.next },
        })
      } else if (result.error === 'invalid_email') {
        setEmailError('Проверьте адрес — в нём ошибка')
      } else if (result.error === 'rate_limited') {
        setEmailError(
          `Слишком часто. Попробуйте через ${Math.ceil((result.retryAfter ?? 60) / 60)} мин.`,
        )
      } else {
        setEmailError('Не получилось отправить код. Попробуйте ещё раз')
      }
    },
    onError: () => setEmailError('Нет связи. Проверьте интернет и попробуйте ещё раз'),
  })

  return (
    <AuthShell
      title={preview.data ? `Вас приглашает ${preview.data.inviterName}` : 'Вход в Snitch'}
      sub={
        fromLink
          ? 'Войдите или создайте аккаунт, чтобы принять приглашение.'
          : 'Один аккаунт для сайта и Telegram. Выберите удобный способ.'
      }
      back={{ to: '/', label: 'На главную' }}
    >
      {fromLink ? (
        <div className="flex flex-col gap-2.5 rounded-[22px] border border-line bg-card p-5">
          {preview.data ? (
            <>
              <div className="flex items-center gap-3.5">
                <span className="font-semibold text-[42px] tracking-[-1px]">
                  −{preview.data.discountPercent}%
                </span>
                <span className="font-medium text-[17px]">
                  на первый
                  <br />
                  обычный тариф
                </span>
              </div>
              <p className="text-ink-2 text-[13px]">{fromLink.code} · код уже подставлен</p>
              <p className="text-ink-2 text-[13px]">
                Только тариф — без теста, LTE и дополнительных мест.
              </p>
            </>
          ) : preview.isError ? (
            <>
              <Alert tone="error">
                Не удалось проверить приглашение. Можно войти и попробовать ещё раз.
              </Alert>
              <button
                type="button"
                className="min-h-11 font-semibold text-sm"
                onClick={() => {
                  forgetInvitation()
                  setFriendCode('')
                  void navigate({ to: '/login', search: {}, replace: true })
                }}
              >
                Продолжить без приглашения
              </button>
            </>
          ) : (
            <p role="status" className="text-sm text-ink-2">
              Проверяем приглашение…
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          <Field
            label="Промокод друга · необязательно"
            icon={<TicketPercent className="text-violet" />}
            name="invite"
            placeholder="Например, FR-8Q2K7M9T"
            value={friendCode}
            maxLength={32}
            autoCapitalize="characters"
            autoComplete="off"
            error={friendError}
            onChange={(e) => {
              setFriendCode(e.target.value)
              setFriendError(undefined)
            }}
          />
          <p className="text-ink-2 text-[13px] leading-[1.4]">
            Учтём приглашение после входа. Код можно добавить и позже — до первого обычного тарифа.
          </p>
        </div>
      )}
      <div className="flex flex-col gap-3">
        {alert ? <Alert tone={alert.tone}>{alert.text}</Alert> : null}
        <Button variant="primary" size="md" block onClick={() => oauth('telegram')}>
          <Send aria-hidden="true" />
          Войти через Telegram
        </Button>
        <Button variant="card" size="md" block onClick={() => oauth('google')}>
          <GoogleIcon className="size-[18px]" />
          Войти через Google
        </Button>
        <Divider>или по почте</Divider>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            setEmailError(undefined)
            const next = target()
            if (next) send.mutate({ email, next })
          }}
        >
          <Field
            label="Почта"
            icon={<Mail />}
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            required
            value={email}
            error={emailError}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button variant="soft" size="md" block type="submit" disabled={send.isPending}>
            {send.isPending ? 'Отправляем…' : 'Получить код'}
          </Button>
        </form>
        <p className="text-ink-3 text-xs leading-[1.45]">
          Продолжая, вы принимаете{' '}
          <a href="/legal/offer" className="underline underline-offset-2">
            оферту
          </a>{' '}
          и{' '}
          <a href="/legal/privacy" className="underline underline-offset-2">
            политику конфиденциальности
          </a>
          . Нет аккаунта — он создастся при первом входе.
        </p>
      </div>
    </AuthShell>
  )
}
